import React, { useState, useEffect } from 'react';
import {
    getRisiken,
    createRisiko,
    updateRisiko,
    deleteRisiko,
    getMassnahmen,
    createMassnahme,
    updateMassnahme,
    deleteMassnahme
} from '../../../../../services/schutzbedarfsanalyseService';
import RisikoModal from './RisikoModal';
import MassnahmenModal from './MassnahmenModal';
import './RisikenSection.css';

const RisikenSection = () => {
    const [risiken, setRisiken] = useState([]);
    const [massnahmen, setMassnahmen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [editingRisiko, setEditingRisiko] = useState(null);
    const [showMassnahmenModal, setShowMassnahmenModal] = useState(false);
    const [selectedRisiko, setSelectedRisiko] = useState(null);
    const [editingMassnahme, setEditingMassnahme] = useState(null);

    // Filter State
    const [filterStufe, setFilterStufe] = useState('alle');
    const [filterBereich, setFilterBereich] = useState('alle');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        const [risikenResult, massnahmenResult] = await Promise.all([
            getRisiken(),
            getMassnahmen()
        ]);

        if (risikenResult.success) {
            setRisiken(risikenResult.data);
        } else {
            setError(risikenResult.error);
        }

        if (massnahmenResult.success) {
            setMassnahmen(massnahmenResult.data);
        }

        setLoading(false);
    };

    const handleCreate = () => {
        setEditingRisiko(null);
        setShowModal(true);
    };

    const handleEdit = (risiko) => {
        setEditingRisiko(risiko);
        setShowModal(true);
    };

    const handleSave = async (risikoData) => {
        let result;

        if (editingRisiko) {
            result = await updateRisiko(editingRisiko.risiko_id, risikoData);
        } else {
            result = await createRisiko(risikoData);
        }

        if (result.success) {
            setShowModal(false);
            setEditingRisiko(null);
            loadData();
        } else {
            alert('Fehler beim Speichern: ' + result.error);
        }
    };

    const handleDelete = async (risikoId) => {
        if (!window.confirm('Risiko wirklich löschen?')) return;

        const result = await deleteRisiko(risikoId);

        if (result.success) {
            loadData();
        } else {
            alert('Fehler beim Löschen: ' + result.error);
        }
    };

    const handleAddMassnahme = (risiko) => {
        setSelectedRisiko(risiko);
        setEditingMassnahme(null);
        setShowMassnahmenModal(true);
    };

    const handleSaveMassnahme = async (massnahmeIdOrData, massnahmeData) => {
        let result;
        
        if (typeof massnahmeIdOrData === 'string') {
            result = await updateMassnahme(massnahmeIdOrData, massnahmeData);
        } else {
            result = await createMassnahme(massnahmeIdOrData);
        }

        if (result.success) {
            setShowMassnahmenModal(false);
            setSelectedRisiko(null);
            setEditingMassnahme(null);
            loadData();
        } else {
            alert('Fehler beim Speichern: ' + result.error);
        }
    };

    const handleEditMassnahme = (massnahme, risiko) => {
        setSelectedRisiko(risiko);
        setEditingMassnahme(massnahme);
        setShowMassnahmenModal(true);
    };

    const handleDeleteMassnahme = async (massnahmeId) => {
        if (!window.confirm('Maßnahme wirklich löschen?')) return;

        const result = await deleteMassnahme(massnahmeId);

        if (result.success) {
            loadData();
        } else {
            alert('Fehler beim Löschen: ' + result.error);
        }
    };


    // ==================== RISIKOMATRIX ====================
    const renderRisikoMatrix = () => {
        // Matrix 4x4 (wie in Skizze)
        const matrix = [];

        for (let ew = 4; ew >= 1; ew--) {
            for (let sh = 1; sh <= 4; sh++) {
                const score = ew * sh;
                let color, borderColor;

                // Farben nach Skizze: 1-3: grün, 4-7: gelb, 8-11: orange, 12-16: rot
                if (score >= 12) {
                    color = 'rgba(239, 68, 68, 0.3)';
                    borderColor = '#ef4444';
                } else if (score >= 8) {
                    color = 'rgba(245, 158, 11, 0.3)';
                    borderColor = '#f59e0b';
                } else if (score >= 4) {
                    color = 'rgba(234, 179, 8, 0.3)';
                    borderColor = '#eab308';
                } else {
                    color = 'rgba(34, 197, 94, 0.3)';
                    borderColor = '#22c55e';
                }

                // Zahl der Risiken in dieser Zelle
                const count = risiken.filter(
                    r => r.eintrittswahrscheinlichkeit === ew && r.schadenshoehe === sh
                ).length;

                matrix.push(
                    <div
                        key={`${ew}-${sh}`}
                        className="matrix-cell"
                        style={{
                            backgroundColor: color,
                            borderColor: borderColor
                        }}
                    >
                        {count > 0 && <span>{count}</span>}
                    </div>
                );
            }
        }

        return (
            <div className="risiko-matrix-container">
                <h3 className="matrix-title">Risikoübersicht (4x4 Matrix)</h3>

                <div className="matrix">
                    {/* Header */}
                    <div className="matrix-header"></div>
                    <div className="matrix-header">1 - Gering</div>
                    <div className="matrix-header">2 - Mittel</div>
                    <div className="matrix-header">3 - Hoch</div>
                    <div className="matrix-header">4 - Sehr Hoch</div>

                    {/* Zeile 4 (sehr häufig) */}
                    <div className="matrix-label">4 - Sehr häufig</div>
                    {matrix.slice(0, 4)}

                    {/* Zeile 3 (häufig) */}
                    <div className="matrix-label">3 - Häufig</div>
                    {matrix.slice(4, 8)}

                    {/* Zeile 2 (gelegentlich) */}
                    <div className="matrix-label">2 - Gelegentlich</div>
                    {matrix.slice(8, 12)}

                    {/* Zeile 1 (selten) */}
                    <div className="matrix-label">1 - Selten</div>
                    {matrix.slice(12, 16)}
                </div>

                <div className="matrix-legend">
                    <div className="legend-item">
                        <div className="legend-color" style={{ background: '#22c55e' }}></div>
                        <span>Gering (1-3)</span>
                    </div>
                    <div className="legend-item">
                        <div className="legend-color" style={{ background: '#eab308' }}></div>
                        <span>Mittel (4-7)</span>
                    </div>
                    <div className="legend-item">
                        <div className="legend-color" style={{ background: '#f59e0b' }}></div>
                        <span>Hoch (8-11)</span>
                    </div>
                    <div className="legend-item">
                        <div className="legend-color" style={{ background: '#ef4444' }}></div>
                        <span>Sehr Hoch (12-16)</span>
                    </div>
                </div>
            </div>
        );
    };

    // ==================== RISIKOSTUFE BADGE ====================
    const getRisikostufeBadge = (stufe) => {
        const colors = {
            'sehr_hoch': '#ef4444',
            'hoch': '#f59e0b',
            'mittel': '#eab308',
            'gering': '#22c55e',
        };

        const labels = {
            'sehr_hoch': 'Sehr Hoch',
            'hoch': 'Hoch',
            'mittel': 'Mittel',
            'gering': 'Gering',
        };

        return (
            <span
                className="risikostufe-badge"
                style={{
                    background: colors[stufe] || '#666',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                }}
            >
                {labels[stufe] || 'Unbekannt'}
            </span>
        );
    };

    // ==================== FILTER ====================
    const filteredRisiken = risiken.filter(r => {
        if (filterStufe !== 'alle' && r.risikostufe !== filterStufe) return false;
        if (filterBereich !== 'alle' && r.risiko_bereich !== filterBereich) return false;
        return true;
    });

    // ==================== RENDER ====================
    if (loading) {
        return (
            <div className="risiken-section">
                <div className="loading-state">Lade Risiken...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="risiken-section">
                <div className="error-state">
                    <p>Fehler: {error}</p>
                    <button onClick={loadData} className="btn-retry">Erneut versuchen</button>
                </div>
            </div>
        );
    }

    return (
        <div className="risiken-section">
            <div className="section-header">
                <div>
                    <h2>Risikomanagement</h2>
                    <p className="content-subtitle">
                        Risiken mit Bewertung und Maßnahmen
                    </p>
                    <br />
                </div>
                <button onClick={handleCreate} className="btn-primary">
                    Neues Risiko
                </button>
            </div>

            {/* Risikomatrix */}
            {renderRisikoMatrix()}

            {/* Filter */}
            <div className="filter-bar" style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <div>
                    <label>Risikostufe:</label>
                    <select value={filterStufe} onChange={e => setFilterStufe(e.target.value)}>
                        <option value="alle">Alle</option>
                        <option value="gering">Gering</option>
                        <option value="mittel">Mittel</option>
                        <option value="hoch">Hoch</option>
                        <option value="sehr_hoch">Sehr Hoch</option>
                    </select>
                </div>
                <div>
                    <label>Bereich:</label>
                    <select value={filterBereich} onChange={e => setFilterBereich(e.target.value)}>
                        <option value="alle">Alle</option>
                        <option value="IT">IT</option>
                        <option value="ISB">ISB</option>
                        <option value="BCM">BCM</option>
                        <option value="Personal">Personal</option>
                    </select>
                </div>
            </div>

            {/* Risikenliste */}
            {filteredRisiken.length === 0 ? (
                <div className="empty-state">
                    <h3>Keine Risiken vorhanden</h3>
                    <p>Erstellen Sie Ihr erstes Risiko</p>
                    <button onClick={handleCreate} className="btn-primary">
                        Risiko erfassen
                    </button>
                </div>
            ) : (
                <div className="risiken-list">
                    {filteredRisiken.map(risiko => {
                        const risikoMassnahmen = massnahmen.filter(m => m.risiko_id === risiko.risiko_id);

                        return (
                            <div key={risiko.risiko_id} className="risiko-card">
                                <div className="risiko-card-header">
                                    <h4>{risiko.risiko_bezeichnung}</h4>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {getRisikostufeBadge(risiko.risikostufe)}
                                        {risiko.risiko_bereich && (
                                            <span style={{
                                                padding: '4px 10px',
                                                background: '#f3f4f6',
                                                color: '#374151',
                                                borderRadius: '12px',
                                                fontSize: '11px',
                                                fontWeight: '500'
                                            }}>
                                                {risiko.risiko_bereich}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {risiko.risiko_beschreibung && (
                                    <p className="risiko-description">{risiko.risiko_beschreibung}</p>
                                )}

                                <div className="risiko-scores">
                                    <div className="score-item">
                                        <span className="score-label">Schadenshöhe</span>
                                        <span className="score-value">{risiko.schadenshoehe}</span>
                                    </div>
                                    <div className="score-item">
                                        <span className="score-label">Eintrittswahrsch.</span>
                                        <span className="score-value">{risiko.eintrittswahrscheinlichkeit}</span>
                                    </div>
                                    <div className="score-item highlight">
                                        <span className="score-label">Risikoscore</span>
                                        <span className="score-value">{risiko.risikoscore}</span>
                                    </div>
                                </div>

                                <div className="risiko-meta">
                                    {risiko.verantwortlicher && (
                                        <span>👤 {risiko.verantwortlicher}</span>
                                    )}
                                    {risiko.behandlungsstatus && (
                                        <span>📋 {risiko.behandlungsstatus}</span>
                                    )}
                                    <span>✅ {risikoMassnahmen.length} Maßnahmen</span>
                                </div>

                                {risikoMassnahmen.length > 0 && (
                                    <div className="massnahmen-list" style={{ marginTop: '16px' }}>
                                        {risikoMassnahmen.map(massnahme => (
                                            <div key={massnahme.massnahme_id} className="massnahme-card">
                                                <div className="massnahme-header">
                                                    <h5 className="massnahme-title">{massnahme.massnahme_titel}</h5>
                                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                        {massnahme.prioritaet && (
                                                            <span className={`prioritaet-badge prioritaet-${massnahme.prioritaet}`}>
                                                                {massnahme.prioritaet}
                                                            </span>
                                                        )}
                                                        {massnahme.status && (
                                                            <span className={`status-badge status-${massnahme.status}`}>
                                                                {massnahme.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {massnahme.massnahme_beschreibung && (
                                                    <p className="massnahme-description">{massnahme.massnahme_beschreibung}</p>
                                                )}
                                                <div className="massnahme-meta">
                                                    {massnahme.verantwortlicher && (
                                                        <span>👤 {massnahme.verantwortlicher}</span>
                                                    )}
                                                    {massnahme.massnahme_typ && (
                                                        <span>🔧 {massnahme.massnahme_typ}</span>
                                                    )}
                                                    {massnahme.geplantes_datum && (
                                                        <span>📅 {new Date(massnahme.geplantes_datum).toLocaleDateString('de-DE')}</span>
                                                    )}
                                                    {massnahme.kosten && (
                                                        <span>💰 {massnahme.kosten.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                                                    )}
                                                </div>
                                                <div className="massnahme-actions" style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                                                    <button 
                                                        onClick={() => handleEditMassnahme(massnahme, risiko)} 
                                                        className="btn-secondary btn-small"
                                                        style={{ fontSize: '11px', padding: '4px 10px' }}
                                                    >
                                                        Bearbeiten
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteMassnahme(massnahme.massnahme_id)} 
                                                        className="btn-ghost btn-small"
                                                        style={{ fontSize: '11px', padding: '4px 10px' }}
                                                    >
                                                        Löschen
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="risiko-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                    <button onClick={() => handleAddMassnahme(risiko)} className="btn-primary btn-small">
                                        + Maßnahme
                                    </button>
                                    <button onClick={() => handleEdit(risiko)} className="btn-secondary btn-small">
                                        Bearbeiten
                                    </button>
                                    <button onClick={() => handleDelete(risiko.risiko_id)} className="btn-ghost btn-small">
                                        Löschen
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <RisikoModal
                    risiko={editingRisiko}
                    onSave={handleSave}
                    onClose={() => {
                        setShowModal(false);
                        setEditingRisiko(null);
                    }}
                />
            )}

            {showMassnahmenModal && selectedRisiko && (
                <MassnahmenModal
                    risiko={selectedRisiko}
                    massnahme={editingMassnahme}
                    onSave={handleSaveMassnahme}
                    onClose={() => {
                        setShowMassnahmenModal(false);
                        setSelectedRisiko(null);
                        setEditingMassnahme(null);
                    }}
                />
            )}
        </div>
    );
};

export default RisikenSection;
